import {
  Spacing,
  FoundationColorTokens,
  Typography as LeoTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { TFunction } from "i18next";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { SettingsContainer } from "../../../../components/SettingsContainer";
import { Stack, Typography } from "@mui/material";
import { Button, Dialog } from "@surya-digital/leo-reactjs-material-ui";
import { Instance } from "mobx-state-tree";
import {
  NavigateToFunctions,
  processSurveyParams,
} from "../../../../routes/RoutesHelper";
import {
  COUNTDOWN_INTERVAL_IN_MS,
  END_OF_COUNTDOWN,
  TOTAL_COUNTDOWN_TIME_IN_SEC,
} from "@pulse/shared-components";
import { BackOfficeSurveySettingsStore } from "../../store/BackOfficeSurveySettingsStore";

interface ResetSurveySettingsSectionProps {
  backOfficeSurveySettingsStore: Instance<typeof BackOfficeSurveySettingsStore>;
  navigateTo: NavigateToFunctions;
  t: TFunction;
  spacing: Spacing;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
}

export const ResetSurveySettingsSection = observer(
  ({
    backOfficeSurveySettingsStore,
    navigateTo,
    t,
    spacing,
    typography,
    tokens,
  }: ResetSurveySettingsSectionProps): React.ReactElement => {
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [countDownValue, setCountDownValue] = useState(
      TOTAL_COUNTDOWN_TIME_IN_SEC,
    );

    useEffect(() => {
      if (!isDialogVisible) {
        setCountDownValue(TOTAL_COUNTDOWN_TIME_IN_SEC);
        return;
      }

      const interval = setInterval(() => {
        setCountDownValue((previousCountDownValue) =>
          previousCountDownValue === END_OF_COUNTDOWN
            ? previousCountDownValue
            : previousCountDownValue - 1,
        );
      }, COUNTDOWN_INTERVAL_IN_MS);

      return () => clearInterval(interval);
    }, [isDialogVisible]);

    const getPrimaryButtonText = (): string | undefined => {
      if (countDownValue === END_OF_COUNTDOWN) {
        return t(
          "surveys.settings.resetSurveySettings.resetSurveyConfirmButtonText",
        );
      }
      return t(
        "surveys.settings.resetSurveySettings.resetSurveyConfirmButtonTextWithCountdown",
        {
          primaryButtonCountdown: countDownValue,
        },
      );
    };

    const isPrimaryButtonDisabled =
      countDownValue !== END_OF_COUNTDOWN ||
      backOfficeSurveySettingsStore.isResetSurveySettingsRPCLoading;

    const isSecondaryButtonDisabled =
      backOfficeSurveySettingsStore.isResetSurveySettingsRPCLoading;

    return (
      <SettingsContainer
        settingHeadingText={t(
          "surveys.settings.resetSurveySettings.resetSurveyTitleText",
        )}
        settingSubHeadingText={t(
          "surveys.settings.resetSurveySettings.resetSurveyDescriptionText",
        )}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
      >
        <Stack width="fit-content">
          <Button
            label={t(
              "surveys.settings.resetSurveySettings.resetSurveyButtonText",
            )}
            name="resetSurveySettings"
            size="medium"
            variant="outlined-color"
            color="destructive"
            onClick={() => setIsDialogVisible(true)}
          />
        </Stack>
        {isDialogVisible && (
          <Dialog
            open={isDialogVisible}
            title={t(
              "surveys.settings.resetSurveySettings.resetSurveyDialogTitle",
            )}
            primaryButtonText={getPrimaryButtonText()}
            secondaryButtonText={t("common.cancel")}
            onPrimaryButtonClick={async (): Promise<void> => {
              await processSurveyParams(
                async (surveyId: string, projectId: string): Promise<void> => {
                  await backOfficeSurveySettingsStore.resetSurveySettings(
                    surveyId,
                    projectId,
                  );
                  if (backOfficeSurveySettingsStore.rpcError === null) {
                    await backOfficeSurveySettingsStore.getSurveySettings(
                      surveyId,
                      projectId,
                    );
                  }
                },
                navigateTo,
              );

              if (backOfficeSurveySettingsStore.rpcError === null) {
                setIsDialogVisible(false);
              }
            }}
            onSecondaryButtonClick={(): void => {
              setIsDialogVisible(false);
            }}
            isPrimaryButtonDisabled={isPrimaryButtonDisabled}
            isSecondaryButtonDisabled={isSecondaryButtonDisabled}
            width="560px"
            contentPadding={spacing.spaceLG}
            disableBackdropClick={true}
          >
            <Stack width="100%" gap={spacing.spaceXS}>
              <Typography {...typography.b2} color={tokens.label} width="100%">
                {t(
                  "surveys.settings.resetSurveySettings.resetSurveyDialogMessage",
                )}
              </Typography>
            </Stack>
          </Dialog>
        )}
      </SettingsContainer>
    );
  },
);
